import DefaultLayout from '@website/components/layouts/DefaultLayout';
import TripsPageContent from '@website/components/trips/TripsPage/TripsPageContent';
import ErrorPage from '@website/components/utilities/ErrorPage';
import { ssrClient, useHydrateApolloCache } from '@website/libs/graphql/apollo';
import {
  GetTripsStaticPathsDocument,
  TripsPageDocument,
  TripsPageQuery,
  TripsSlugHistoryDocument,
} from '@website/types/graphqlOperations';
import {
  GetStaticPathsResult,
  GetStaticPropsContext,
  GetStaticPropsResult,
} from 'next';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { PropsWithChildren } from 'react';
import LoadingPage from '../../../components/utilities/LoadingPage';

const ToastifyProvider = dynamic(
  () =>
    import('@website/components/SalesPopupProvider').then(
      (mod) => mod.SalesPopupProvider,
    ),
  { ssr: false },
);

type StaticTripUrlsParams = {
  tripUrl: string;
};

/**
 * Get static paths
 */
export async function getStaticPaths(): Promise<
  GetStaticPathsResult<StaticTripUrlsParams>
> {
  const client = ssrClient();

  const { data } = await client.query({
    query: GetTripsStaticPathsDocument,
  });

  const paths = data.trips.items
    .filter((trip) => !!trip.urlSlug)
    .map((trip) => ({
      params: {
        tripUrl: trip.urlSlug,
      },
    }));

  return {
    paths,
    // Enable fallback so that new trips will be generated on first load, rather than waiting for a new deploy
    fallback: true,
  };
}

/**
 * Get static props
 */
export async function getStaticProps({
  params: { tripUrl: urlSlug },
}: GetStaticPropsContext<StaticTripUrlsParams>): Promise<
  GetStaticPropsResult<{
    urlSlug: string;
    data: TripsPageQuery;
  }>
> {
  const client = ssrClient();

  if (!urlSlug) {
    return { notFound: true };
  }

  let data: TripsPageQuery;
  try {
    const res = await client.query({
      query: TripsPageDocument,
      variables: { urlSlug },
    });
    data = res.data;

    console.log('data', data);
  } catch (e) {
    console.error(e);
    // If the trip was not found, check if the slug was previously used and
    // redirect to the new slug
    if (e.graphQLErrors[0].message.includes('was not found')) {
      const res = await client.query({
        query: TripsSlugHistoryDocument,
        variables: {
          urlSlug,
        },
      });

      if (res.data.fetchTripByPreviousSlug) {
        return {
          redirect: {
            destination: `/trips/${res.data.fetchTripByPreviousSlug.trip.slug}`,
            permanent: true,
          },
        };
      }

      // If the slug was not previously used, return a 404
      return {
        notFound: true,
      };
    }
  }

  return {
    props: {
      urlSlug,
      data: data ?? null,
    },
    // every 10 minutes
    revalidate: 600,
  };
}

/**
 * Main Trip Page Component
 * @param {Props} props
 */
export default function TripPage({
  urlSlug,
  data,
}: {
  urlSlug: string;
  data: TripsPageQuery;
}) {
  useHydrateApolloCache({
    data,
    query: TripsPageDocument,
    variables: { urlSlug },
  });

  const router = useRouter();

  // show loading page if the page hasn't been generated, fallback will generate
  // send it to the frontend
  if (router.isFallback) {
    return <LoadingPage />;
  }

  if (!urlSlug) {
    return <ErrorPage error="Trip url slug can not be undefined!" />;
  }

  return (
    <>
      <TripsPageContent urlSlug={urlSlug} />
      <ToastifyProvider />
    </>
  );
}

TripPage.Layout = function Layout({ children }: PropsWithChildren<{}>) {
  return <DefaultLayout className="">{children}</DefaultLayout>;
};
